/**
 * Booking-link decision logic for the interview invitation.
 *
 * A booking link is any URL a candidate can use to pick a slot: Calendly,
 * Cal.com, Google Appointments, Microsoft Bookings, a self-hosted page. The
 * recruiter has one saved default (a user-level setting); the popover lets
 * them include it, exclude it, or override it for a single send.
 *
 * Kept free of React so the rules are unit-testable and so the same shape can
 * back any future pipeline-stage action, not just the acceptance email.
 */

/** Provider key and label are resolved by the backend registry, never here. */
export interface SavedBookingLink {
  url: string;
  provider: string;
  provider_label: string;
  include_by_default: boolean;
}

/** What the popover currently shows. */
export interface BookingLinkDraft {
  /** The "include a booking link" checkbox. */
  include: boolean;
  /** The link as typed or as loaded from the saved default. */
  url: string;
  /** Whether the URL field is shown rather than the read-only saved link. */
  editing: boolean;
}

/** Body fields for POST /api/candidates/{id}/accept. */
export interface AcceptCandidatePayload {
  include_booking_link: boolean;
  booking_link?: string;
  /** Per-move opt-out for the acceptance email; the backend defaults to true. */
  send_notification?: boolean;
}

const MAX_URL_LENGTH = 2048;

/**
 * Canonical form of a pasted link. A bare host is promoted to https, which is
 * by far the most common paste. Mirrors the backend's normalisation so the
 * value the recruiter sees is the value that gets stored.
 */
export const normalizeBookingUrl = (raw: string): string => {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const isValidBookingUrl = (raw: string): boolean => {
  const normalized = normalizeBookingUrl(raw);
  if (!normalized || normalized.length > MAX_URL_LENGTH) return false;

  let parsed: URL;
  try {
    parsed = new URL(normalized);
  } catch {
    return false;
  }

  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
  return parsed.hostname.includes('.') && !/\s/.test(normalized);
};

/**
 * How the popover opens: with a saved default it is pre-ticked and shows the
 * link read-only; with nothing saved it is unticked with an empty field.
 */
export const initialBookingDraft = (saved: SavedBookingLink | null): BookingLinkDraft =>
  saved
    ? { include: saved.include_by_default, url: saved.url, editing: false }
    : { include: false, url: '', editing: true };

/** Ticking the box with no usable link is the only thing that blocks a send. */
export const canSendInvitation = (draft: BookingLinkDraft): boolean =>
  !draft.include || isValidBookingUrl(draft.url);

export const acceptCandidatePayload = (
  draft: BookingLinkDraft,
  saved: SavedBookingLink | null,
): AcceptCandidatePayload => {
  if (!draft.include || !isValidBookingUrl(draft.url)) {
    return { include_booking_link: false };
  }

  const normalized = normalizeBookingUrl(draft.url);
  if (saved && normalized === saved.url) {
    // Unchanged: let the server resolve the saved default.
    return { include_booking_link: true };
  }
  return { include_booking_link: true, booking_link: normalized };
};

/**
 * Whether to offer "save as my default". Only when replacing an existing
 * default — the very first link a recruiter types is captured silently by the
 * backend, so asking would be noise.
 */
export const canOfferSaveAsDefault = (
  draft: BookingLinkDraft,
  saved: SavedBookingLink | null,
): boolean =>
  Boolean(saved) &&
  draft.include &&
  isValidBookingUrl(draft.url) &&
  normalizeBookingUrl(draft.url) !== saved!.url;
